/**
 * Shared types for the Cleanup feedback experience (#654, extended for the
 * Apply-job companion experiment in #951).
 */

/**
 * Experiment cohort, resolved from the same PostHog feature flag used since
 * #654 (`docs/MARKETING_EMAIL_AUTOMATION.md` / experience-plan.md "reuse the
 * existing feedback timing flag"):
 *  - `manual_only`: persistent manual action only (control).
 *  - `manual_plus_timed`: adds the 45s timed nudge (control, #654).
 *  - `apply_job_companion`: adds the Variant A Apply-job companion instead of
 *    the 45s nudge (#951 treatment). The two automatic invitations are
 *    mutually exclusive per cohort.
 */
export type CleanupFeedbackVariant = 'manual_only' | 'manual_plus_timed' | 'apply_job_companion';

/**
 * Converts the configured PostHog feature-flag value into the experiment
 * cohort. Unknown, boolean, or unavailable values fail closed to manual-only.
 */
export function resolveCleanupFeedbackVariant(value: unknown): CleanupFeedbackVariant {
  if (value === 'manual_plus_timed' || value === 'apply_job_companion') {
    return value;
  }
  return 'manual_only';
}

/** What opened the survey shell. */
export type CleanupFeedbackOpenTrigger = 'manual' | '45s' | 'apply';

/** Delay before popping up the feedback survey after an apply action. */
export const CLEANUP_FEEDBACK_APPLY_DELAY_MS = 2000;

/** Cumulative visible seconds required before the automatic callout appears. */
export const CLEANUP_FEEDBACK_NUDGE_THRESHOLD_SECONDS = 45;

/** Auto-expiry window for the automatic callout once shown. */
export const CLEANUP_FEEDBACK_NUDGE_EXPIRY_MS = 8000;

/**
 * Point at which the character counter becomes visible. The maximum length
 * itself is `PRODUCT_FEEDBACK_MAX_MESSAGE_LENGTH` in `@purebox/shared`, shared
 * with the Zod schema and the `product_feedback` CHECK constraint (#707).
 */
export const CLEANUP_FEEDBACK_SHOW_REMAINING_AT = 100;
